import { useEffect, useState } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Plus, Tag, Workflow } from "lucide-react";
import { useUser } from "@/components/UserProvider";
import { AppPageShell } from "@/components/ui/AppPageShell/AppPageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  automationsQueryKey,
  automationDetailQueryOptions,
  automationsQueryOptions,
  contentTagsQueryKey,
  contentTagsQueryOptions,
} from "@/lib/data/react-query/automations";
import {
  deleteAutomation,
  deleteContentTag,
  setAutomationEnabled,
} from "@/lib/data/requests/automations";
import type {
  AutomationSummary,
  AutomationScope,
  ContentTag,
} from "@/lib/data/schemas/automations";
import { AutomationCards } from "./AutomationCards";
import { AutomationEditor } from "./AutomationEditor";
import { ContentTagEditor } from "./ContentTagEditor";
import { AutomationRunPicker } from "./AutomationRunPicker";
import { AutomationActivity } from "./AutomationActivity";
import { automationError } from "./automationError";

type Panel =
  | { kind: "automation"; value?: AutomationSummary }
  | { kind: "tag"; value?: ContentTag }
  | { kind: "activity"; value: AutomationSummary }
  | { kind: "run"; value: AutomationSummary }
  | {
      kind: "delete";
      resource: "automation" | "tag";
      value: AutomationSummary | ContentTag;
    };
export function AutomationsPage() {
  const { hasPagePermission } = useUser();
  const canManageFirm = hasPagePermission("automations.firm");
  const canManagePersonal = hasPagePermission("automations.personal");
  if (!canManageFirm && !canManagePersonal)
    return (
      <div className="p-8">
        You don’t have permission to manage automations.
      </div>
    );
  return (
    <AutomationsContent
      canManageFirm={canManageFirm}
      canManagePersonal={canManagePersonal}
    />
  );
}
function AutomationsContent({
  canManageFirm,
  canManagePersonal,
}: {
  canManageFirm: boolean;
  canManagePersonal: boolean;
}) {
  const [scope, setScope] = useState<AutomationScope>(
    canManagePersonal ? "user" : "firm",
  );
  const availableScopes: AutomationScope[] = [
    ...(canManagePersonal ? ["user" as const] : []),
    ...(canManageFirm ? ["firm" as const] : []),
  ];
  const [view, setView] = useState<"automations" | "tags">("automations");
  const [search, setSearch] = useState("");
  const [panel, setPanel] = useState<Panel>();
  const client = useQueryClient();
  const automations = useInfiniteQuery(automationsQueryOptions(scope));
  const tags = useInfiniteQuery(contentTagsQueryOptions());
  const {
    hasNextPage: hasMoreTags,
    isFetchingNextPage: loadingMoreTags,
    isFetchNextPageError: tagPageFailed,
    fetchNextPage: fetchMoreTags,
  } = tags;
  useEffect(() => {
    if (hasMoreTags && !loadingMoreTags && !tagPageFailed) void fetchMoreTags();
  }, [hasMoreTags, loadingMoreTags, tagPageFailed, fetchMoreTags]);
  const allTags = tags.data?.pages.flatMap((page) => page.items) ?? [];
  const allAutomations =
    automations.data?.pages.flatMap((page) => page.items) ?? [];
  const toggle = useMutation({
    mutationFn: ({
      id,
      enabled,
      revision,
    }: {
      id: string;
      enabled: boolean;
      revision: string;
    }) => setAutomationEnabled(id, enabled, revision),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: automationsQueryKey }),
  });
  const remove = useMutation({
    mutationFn: ({
      resource,
      id,
    }: {
      resource: "automation" | "tag";
      id: string;
    }) =>
      resource === "automation" ? deleteAutomation(id) : deleteContentTag(id),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: automationsQueryKey }),
        client.invalidateQueries({ queryKey: contentTagsQueryKey }),
      ]);
      setPanel(undefined);
    },
  });
  const activeQuery = view === "automations" ? automations : tags;
  const matches = (name: string) =>
    name.toLowerCase().includes(search.toLowerCase());
  return (
    <AppPageShell contentClassName="overflow-y-auto">
      <div className="mx-auto w-full max-w-5xl space-y-6 pb-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Automations</h1>
            <p className="text-muted-foreground mt-2 text-sm">
              Turn processed files into useful work, with control over every
              kind of change.
            </p>
          </div>
          {(view === "automations" || canManageFirm) && (
            <Button
              onClick={() => {
                setPanel({
                  kind: view === "automations" ? "automation" : "tag",
                });
              }}
            >
              <Plus />
              {view === "automations" ? "Create automation" : "Create tag"}
            </Button>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-b pb-4">
          <div className="flex gap-2">
            <Button
              variant={view === "automations" ? "secondary" : "ghost"}
              onClick={() => {
                setView("automations");
                setSearch("");
              }}
            >
              <Workflow />
              Automations
            </Button>
            <Button
              variant={view === "tags" ? "secondary" : "ghost"}
              onClick={() => {
                setView("tags");
                setSearch("");
              }}
            >
              <Tag />
              {canManageFirm ? "Manage tags" : "Content tags"}
            </Button>
          </div>
          <Input
            aria-label={`Search ${view}`}
            placeholder={
              view === "automations" ? "Find an automation…" : "Find a tag…"
            }
            className="w-64"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
            }}
          />
        </div>
        {view === "automations" && (
          <div
            className="flex gap-2"
            role="group"
            aria-label="Automation ownership"
          >
            {canManagePersonal && (
              <Button
                variant={scope === "user" ? "secondary" : "ghost"}
                onClick={() => {
                  setScope("user");
                  setSearch("");
                }}
              >
                Personal automations
              </Button>
            )}
            {canManageFirm && (
              <Button
                variant={scope === "firm" ? "secondary" : "ghost"}
                onClick={() => {
                  setScope("firm");
                  setSearch("");
                }}
              >
                Firm automations
              </Button>
            )}
          </div>
        )}
        {activeQuery.isPending && <p role="status">Loading…</p>}
        {activeQuery.error && (
          <div role="alert" className="text-destructive">
            {automationError(activeQuery.error)}{" "}
            <Button
              variant="outline"
              onClick={() => void activeQuery.refetch()}
            >
              Try again
            </Button>
          </div>
        )}
        {toggle.error && (
          <p role="alert" className="text-destructive">
            {automationError(toggle.error)}
          </p>
        )}
        {view === "automations" ? (
          <AutomationCards
            items={allAutomations.filter((item) => matches(item.name))}
            busy={toggle.isPending}
            onToggle={(item, enabled) => {
              toggle.mutate({
                id: item.id,
                enabled,
                revision: item.currentRevisionId,
              });
            }}
            onEdit={(value) => {
              setPanel({ kind: "automation", value });
            }}
            onActivity={(value) => {
              setPanel({ kind: "activity", value });
            }}
            onRun={(value) => {
              setPanel({ kind: "run", value });
            }}
            onDelete={(value) => {
              remove.reset();
              setPanel({ kind: "delete", resource: "automation", value });
            }}
          />
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {allTags
              .filter((item) => matches(item.name))
              .map((item) => (
                <article
                  key={item.id}
                  className="bg-card rounded-xl border p-5"
                >
                  <h2 className="flex items-center gap-2 font-semibold">
                    <span
                      className="h-3 w-3 rounded-full"
                      style={{ backgroundColor: item.color }}
                    />
                    {item.name}
                  </h2>
                  <p className="text-muted-foreground mt-2 text-sm">
                    {item.description}
                  </p>
                  {item.applicationGuidance && (
                    <p className="mt-3 text-sm">
                      <span className="font-medium">When to apply: </span>
                      {item.applicationGuidance}
                    </p>
                  )}
                  {canManageFirm && (
                    <div className="mt-4 flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setPanel({ kind: "tag", value: item });
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          remove.reset();
                          setPanel({
                            kind: "delete",
                            resource: "tag",
                            value: item,
                          });
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  )}
                </article>
              ))}
          </div>
        )}
        {!activeQuery.isPending &&
          !activeQuery.error &&
          !(view === "automations" ? allAutomations : allTags).some((item) =>
            matches(item.name),
          ) && (
            <div className="rounded-xl border border-dashed p-12 text-center">
              <p className="font-medium">
                {search
                  ? "No matches on this page"
                  : view === "automations"
                    ? "Let your files start the work"
                    : "Teach AI how to recognize your files"}
              </p>
              <p className="text-muted-foreground mt-2 text-sm">
                {search
                  ? "Try another name or load more results."
                  : view === "automations"
                    ? "Create a tag, choose what should happen, and allow the changes it needs."
                    : "Add a name and description. Optional guidance makes the classification more precise."}
              </p>
            </div>
          )}
        {activeQuery.hasNextPage && (
          <Button
            variant="outline"
            disabled={activeQuery.isFetchingNextPage}
            onClick={() => void activeQuery.fetchNextPage()}
          >
            {activeQuery.isFetchingNextPage ? "Loading…" : "Load more"}
          </Button>
        )}
        <Dialog
          open={!!panel}
          onOpenChange={(open) => {
            if (!open && !remove.isPending) setPanel(undefined);
          }}
        >
          <DialogContent
            className={`max-h-[90vh] overflow-y-auto ${panel?.kind === "activity" ? "rounded-2xl sm:max-w-5xl" : "sm:max-w-3xl"} ${panel?.kind === "automation" ? "gap-8 p-6 sm:p-8" : ""}`}
            {...(panel?.kind === "automation" || panel?.kind === "activity"
              ? { "aria-describedby": undefined }
              : {})}
          >
            <DialogHeader>
              <DialogTitle>
                {panel?.kind === "automation"
                  ? panel.value
                    ? "Edit automation"
                    : "Create automation"
                  : panel?.kind === "tag"
                    ? panel.value
                      ? "Edit tag"
                      : "Create tag"
                    : panel?.kind === "activity" || panel?.kind === "run"
                      ? panel.value.name
                      : "Delete item?"}
              </DialogTitle>
              {panel?.kind !== "automation" && panel?.kind !== "activity" && (
                <DialogDescription>
                  {panel?.kind === "tag"
                    ? "These definitions guide AI classification of new files."
                    : "Review this change before continuing."}
                </DialogDescription>
              )}
            </DialogHeader>
            {panel?.kind === "automation" && (
              <>
                {tags.hasNextPage && (
                  <Button
                    variant="outline"
                    onClick={() => void tags.fetchNextPage()}
                    disabled={tags.isFetchingNextPage}
                  >
                    Load more tags
                  </Button>
                )}
                {tags.isPending || tags.hasNextPage ? (
                  <p>Loading tags…</p>
                ) : tags.error ? (
                  <p role="alert">{automationError(tags.error)}</p>
                ) : (
                  <AutomationConfiguration
                    key={panel.value?.id ?? "new"}
                    automationId={panel.value?.id}
                    initialScope={scope}
                    availableScopes={availableScopes}
                    tags={allTags}
                    onClose={() => {
                      setPanel(undefined);
                    }}
                  />
                )}
              </>
            )}
            {panel?.kind === "tag" && (
              <ContentTagEditor
                tag={panel.value}
                onClose={() => {
                  setPanel(undefined);
                }}
              />
            )}
            {panel?.kind === "run" && (
              <AutomationRunPicker
                id={panel.value.id}
                onStarted={() => {
                  setPanel({ kind: "activity", value: panel.value });
                }}
              />
            )}
            {panel?.kind === "activity" && (
              <AutomationActivity id={panel.value.id} />
            )}
            {panel?.kind === "delete" && (
              <div className="space-y-4">
                <p>
                  Delete “{panel.value.name}”?{" "}
                  {panel.resource === "automation"
                    ? "New files will no longer trigger it. Existing run history is retained."
                    : "Tags used by automations must be removed from their triggers first."}
                </p>
                {remove.error && (
                  <p role="alert" className="text-destructive">
                    {automationError(remove.error)}
                  </p>
                )}
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    disabled={remove.isPending}
                    onClick={() => {
                      setPanel(undefined);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    disabled={remove.isPending}
                    onClick={() => {
                      remove.mutate({
                        resource: panel.resource,
                        id: panel.value.id,
                      });
                    }}
                  >
                    {remove.isPending ? "Deleting…" : "Delete"}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AppPageShell>
  );
}

function AutomationConfiguration({
  automationId,
  initialScope,
  availableScopes,
  tags,
  onClose,
}: {
  automationId?: string;
  initialScope: AutomationScope;
  availableScopes: AutomationScope[];
  tags: ContentTag[];
  onClose: () => void;
}) {
  const detail = useQuery({
    ...automationDetailQueryOptions(automationId ?? ""),
    enabled: !!automationId,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  if (automationId && (detail.isPending || detail.isFetching))
    return <p role="status">Loading automation…</p>;
  if (automationId && detail.error)
    return (
      <div role="alert">
        {automationError(detail.error)}{" "}
        <Button variant="outline" onClick={() => void detail.refetch()}>
          Try again
        </Button>
      </div>
    );
  return (
    <AutomationEditor
      key={automationId ?? "new"}
      automation={detail.data}
      initialScope={initialScope}
      availableScopes={availableScopes}
      tags={tags}
      onClose={onClose}
    />
  );
}
